/* eslint-disable max-len */
import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import {
  trackMiniCartProductClick,
  trackMiniCartATBClick,
} from 'utils/tracking';
import { Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import Image from 'utils/image';
import updateURL from 'utils/update-url';
import setAttributes from 'utils/set-attributes';
import { withCartContext } from './cart-context';
import { calculateMaxQuantityAccessible } from './upsells-service';

const Upsells = ({ cart, upsellPromos, title, setUpsells }) => {
  const [isAdded, setIsAdded] = useState(false);
  const [carouselUpsells, setCarouselUpsells] = useState([]);
  const isWideScreen = window.innerWidth > 768;

  useEffect(() => {
    if (!cart.state?.items?.length) return;
    const excludeVariants = upsellPromos
      .filter((u) => u.type.includes('threshold'))
      .map((u) => u.first_available_variant_id);
    const closedUpsells = sessionStorage.getItem('ups_items') || [];

    setUpsells(
      upsellPromos
        .filter(
          (upsellPromo) =>
            upsellPromo.available &&
            !closedUpsells?.includes(
              upsellPromo.first_available_variant_id * 0.22,
            ),
        )
        .map((upsellPromo) => ({
          id: upsellPromo.first_available_variant_id,
          maxQuantityAccessible: calculateMaxQuantityAccessible(
            upsellPromo,
            cart.state,
            excludeVariants,
          ),
          type: upsellPromo.type,
          appliedMsg: upsellPromo.applied_message,
          tooltipMsg: upsellPromo.tooltip_message,
          showQuantitySelector: upsellPromo.show_quantity_selector,
          automaticallyDelete: upsellPromo.automatically_delete,
          subtitle: upsellPromo.subtitle,
          showRemoveLink: upsellPromo.show_remove_link,
          requiresExactQtyToTypeMatch: upsellPromo.requires_exact_qty_to_type_match,
          productTypesRequired: upsellPromo.product_types_required,
          qtyRequired: upsellPromo.quantity_required
        })),
    );

    setCarouselUpsells(
      upsellPromos.filter((upsellPromo) => {
        if (upsellPromo.type.includes('carousel') && upsellPromo.available) {
          const maxQuantityAccessible = calculateMaxQuantityAccessible(
            upsellPromo,
            cart.state,
            excludeVariants,
          );
          const currentQuantity = cart.state.items.reduce(
            (acc, i) => (i.id === upsellPromo.id ? acc + i.quantity : acc),
            0,
          );
          Object.assign(upsellPromo, {
            thresholdReached:
              maxQuantityAccessible > 0 &&
              maxQuantityAccessible !== currentQuantity,
          });
          return true;
        }
        return false;
      }),
    );
  }, [cart.state]);

  const handleAddToCart = (product, quantity, e) => {
    e.preventDefault();
    let productPayload = window.getPayload(product.variants[0], quantity);
    const attributes = setAttributes(product);

    productPayload = {
      ...productPayload,
      attributes,
    };

    const addToCartEvent = new CustomEvent('cart:add', {
      detail: {
        payload: productPayload,
      },
    });

    trackMiniCartATBClick({
      item: product.title,
      itemId: product.variants[0].sku,
    });

    document.dispatchEvent(addToCartEvent);
    setIsAdded(true);

    setTimeout(() => {
      setIsAdded(false);
    }, 1000);
  };

  const handleClick = (product) => {
    trackMiniCartProductClick({
      item: product.title,
      itemId: product.variants[0].sku,
      text: title,
    });

    if (product.show_link) {
      window.location = updateURL(
        `${Shopify.routes.root}products/${product.handle}`,
      );
    }
  };

  const textClick = (product) => {
    trackMiniCartProductClick({
      item: product.upsell_text,
    });
    window.location = updateURL(`${product.upsell_link}`);
  };

  const toggleTooltip = (e) => {
    const tooltipElement = e.target
      .closest('.swiper-slide')
      .querySelector('[data-promo-tooltip]');
    tooltipElement?.classList.toggle('opacity-100');
    tooltipElement?.classList.toggle('opacity-0');
  };

  const showTooltip = (e) => {
    const tooltipElement = e.target
      .closest('.swiper-slide')
      .querySelector('[data-promo-tooltip]');
    tooltipElement?.classList.remove('opacity-0');
    tooltipElement?.classList.add('opacity-100');
  };

  const hideTooltip = (e) => {
    const tooltipElement = e.target
      .closest('.swiper-slide')
      .querySelector('[data-promo-tooltip]');
    tooltipElement?.classList.remove('opacity-100');
    tooltipElement?.classList.add('opacity-0');
  };

  return (
    <Swiper
      slidesPerView={isWideScreen && carouselUpsells.length > 1 ? 1.2 : 1}
      modules={[Scrollbar]}
      spaceBetween={isWideScreen ? 10 : 6}
      scrollbar={{ draggable: true }}
      className="pb-5 nosto-carousel"
    >
      {carouselUpsells.map((product) => (
        <SwiperSlide
          className="h-auto py-5 px-3.5 rounded-md bg-white"
          key={product.id}
        >
          <div className="flex">
            <div
              className="w-[92px] max-w-[92px] basis-[92px] h-[92px] overflow relative mr-2.5"
              style={{ backgroundColor: product.background }}
              onClick={() => handleClick(product)}
            >
              <Image
                src={`${
                  product.image_prod
                    ? `${product.image_prod}`
                    : `${product.featured_image}`
                }`}
                alt={product.title}
                srcWidths={{ sm: 200 }}
                classes="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 object-cover"
              />
            </div>
            <div className="flex flex-col min-h-full basis-[calc(100%-92px)] max-w-[calc(100%-92px)]">
              <h4
                className="text-headline-tag"
                onClick={() => handleClick(product)}
              >
                {`${
                  product.title_prod
                    ? `${product.title_prod}`
                    : `${product.title}`
                }`}
              </h4>
              {product.promo && (
                <div
                  className="text-body-light-2"
                  dangerouslySetInnerHTML={{ __html: product.promo }}
                ></div>
              )}
              {product.upsell_text && product.upsell_link ? (
                <p
                  onClick={() => textClick(product)}
                  onKeyDown={() => textClick(product)}
                  className="underline text-lg capitalize cursor-pointer leading-none"
                  role="button"
                  tabIndex="0"
                >
                  {product.upsell_text}
                </p>
              ) : (
                <div className="relative">
                  {product.tooltip_unqualified && !product.thresholdReached && (
                    <p
                      data-promo-tooltip
                      className={`absolute bottom-[50px] -left-[10px] w-full p-2
                    max-w-[160px] opacity-0 bg-black text-white
                    text-sm rounded-md transition-opacity ease-in-out
                    duration-75
                    before:content-[''] before:w-3 before:h-3
                    before:bg-black before:absolute 
                    before:-bottom-1 before:left-[50%]
                    before:rotate-45 before:-translate-x-[50%]
                    ${
                      isWideScreen
                        ? 'text-center'
                        : `pr-5 after:content-close after:block after:absolute
                    after:right-2 after:top-2.5`
                    }`}
                      onClick={(e) => hideTooltip(e)}
                      dangerouslySetInnerHTML={{
                        __html: product.tooltip_unqualified,
                      }}
                    ></p>
                  )}
                  <button
                    className={`button button--secondary mt-3 border-0 w-fit min-w-[136px] p-3 
                  ${
                    !product.variants[0].available ? 'pointer-events-none' : ''
                  } 
                  ${!product.thresholdReached ? 'opacity-50' : ''}`}
                    onClick={(e) =>
                      product.thresholdReached
                        ? handleAddToCart(product, 1, e)
                        : !isWideScreen && toggleTooltip(e)
                    }
                    onMouseEnter={(e) =>
                      !product.thresholdReached && isWideScreen
                        ? showTooltip(e)
                        : false
                    }
                    onMouseOut={(e) =>
                      !product.thresholdReached && isWideScreen
                        ? hideTooltip(e)
                        : false
                    }
                  >
                    {isAdded
                      ? window.TMW.Strings.product.added
                      : !product.variants[0].available
                      ? window.TMW.Strings.product.outOfStock
                      : window.TMW.Strings.product.addToCart}
                  </button>
                </div>
              )}
            </div>
          </div>
        </SwiperSlide>
      ))}
    </Swiper>
  );
};

export default withCartContext(Upsells);
